"use client";

import * as React from "react";

/**
 * The words the shared UI primitives render on their own — a dialog's
 * screen-reader "Close", the sidebar toggle, a data table's pager — in one
 * place an app can translate once.
 *
 * English is the default, so every app that never mounts a provider renders
 * exactly what it did before. An app that serves people in another language
 * (the staff portal, in the employee's POS language) wraps its tree in
 * {@link CommonLabelsProvider} with its own translation and every primitive
 * below it follows, with no prop threaded through each call site.
 *
 * `{name}` placeholders are filled with `fillPlaceholders` from `@order-up/models`
 * by the primitive that renders them.
 */
export const COMMON_LABELS_EN = {
  /** Dialog and sheet close button (screen-reader text). */
  close: "Close",
  /** The mobile sidebar sheet's close button. */
  closeNavigation: "Close navigation",
  /** Sidebar trigger and rail. */
  toggleSidebar: "Toggle Sidebar",
  /** The mobile sidebar sheet's hidden title and description. */
  sidebarTitle: "Sidebar",
  sidebarDescription: "Displays the mobile sidebar.",
  /** A loading region's accessible name. */
  loading: "Loading",
  table: {
    noResults: "No results.",
    showDetails: "Show details",
    sortBy: "Sort by",
    defaultOrder: "Default order",
    /** Appended to a sortable column header for screen readers. */
    sortSuffix: ", sort",
    selected: "{selected} of {total} selected",
    rowsPerPage: "Rows per page",
    page: "Page {page} of {pages}",
    previousPage: "Previous page",
    nextPage: "Next page",
  },
};

export type CommonLabels = typeof COMMON_LABELS_EN;

const CommonLabelsContext = React.createContext<CommonLabels>(COMMON_LABELS_EN);

export function CommonLabelsProvider({
  labels,
  children,
}: {
  labels: CommonLabels;
  children: React.ReactNode;
}) {
  return (
    <CommonLabelsContext.Provider value={labels}>
      {children}
    </CommonLabelsContext.Provider>
  );
}

/** The labels for the nearest provider, or English outside one. */
export function useCommonLabels(): CommonLabels {
  return React.useContext(CommonLabelsContext);
}
